import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { getMyPermissions } from "../api/users";
import { getUnreadSummary } from "../api/messages";

// `perm` is the feature permission required to see a nav item; items without
// one are visible to everyone. Admins see everything. Items are grouped into
// operational sections for a dense, scannable sidebar.
interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  perm?: string;
  adminOnly?: boolean;
  alwaysVisible?: boolean;
  // Show only for users on this billing mode (admins always see it). Used by the
  // Rates page, which only describes how 'npa_nxx'-billed users are charged.
  billingMode?: string;
}
interface NavGroup {
  title: string | null;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    title: null,
    items: [
      { to: "/dashboard", label: "Dashboard", end: true },
      { to: "/messages", label: "Messages / Inbox", alwaysVisible: true },
    ],
  },
  {
    title: "Outbound",
    items: [
      { to: "/campaigns", label: "Campaigns", perm: "campaign" },
      { to: "/contacts", label: "Contacts", perm: "contact" },
      { to: "/dnc", label: "Do-Not-Call", perm: "contact" },
      { to: "/dids", label: "Phone Numbers", perm: "did" },
      { to: "/dids/acquire", label: "DID Targets", perm: "did" },
      { to: "/dids/spam-detection", label: "Spam Detection Queue", perm: "did" },
      { to: "/callerid-groups", label: "Caller ID Groups", perm: "did" },
      { to: "/consent-sms", label: "Consent SMS", perm: "consent_manage" },
      { to: "/dids/complaints", label: "Caller-ID Complaints", adminOnly: true },
      { to: "/number-lookup", label: "Number Lookup (LNP)", perm: "lnp_lookup" },
      { to: "/recordings", label: "Campaign Audio", perm: "recording_listen" },
      { to: "/timeslots", label: "Calling Windows", perm: "timeslot" },
      { to: "/templates", label: "Templates", perm: "template" },
      { to: "/ivr", label: "IVR Designer", perm: "ivr" },
    ],
  },
  {
    title: "Live",
    items: [
      { to: "/agent", label: "Agent Panel", perm: "agent" },
      { to: "/agents", label: "My Agents", perm: "agent" },
      { to: "/queue-status", label: "Queue Status", perm: "campaign_queuestatus" },
      { to: "/leads", label: "Captured Leads", perm: "contact_leads" },
      { to: "/call-recordings", label: "Call Recordings", perm: "recording_calls" },
      { to: "/transfer-destinations", label: "Transfer Destinations", perm: "agent" },
      { to: "/evaluation", label: "Evaluation", perm: "evaluation" },
    ],
  },
  {
    title: "Reporting",
    items: [
      { to: "/reports", label: "Reports", perm: "report" },
      { to: "/workforce", label: "Workforce", perm: "report_workforce" },
      { to: "/scorecard", label: "Agent Performance", perm: "report_workforce" },
      { to: "/rates", label: "Rates", billingMode: "npa_nxx" },
      { to: "/activity", label: "Activity Log", perm: "loghistory" },
    ],
  },
  {
    title: "Support",
    items: [
      { to: "/assistant", label: "Assistant" },
      { to: "/changelog", label: "Changelog" },
      { to: "/requests", label: "Feature Requests" },
    ],
  },
  {
    title: "Telephony",
    items: [
      { to: "/trunks", label: "Trunks", perm: "trunk" },
      { to: "/routes", label: "Routes", perm: "route" },
      { to: "/destinations", label: "Destinations", perm: "destination" },
      { to: "/extensions", label: "Extensions", perm: "extension" },
      { to: "/geography", label: "Geography", perm: "geography" },
      { to: "/musiconhold", label: "Music on Hold", perm: "musiconhold" },
      { to: "/dialogues", label: "AI Dialogues", perm: "dialogue" },
      { to: "/documents", label: "Documents", perm: "document" },
    ],
  },
  {
    title: "Billing",
    items: [
      { to: "/plans", label: "Plans", perm: "plan" },
      { to: "/billing", label: "Billing", perm: "billing" },
      { to: "/invoicing", label: "Invoicing", adminOnly: true },
      { to: "/clients", label: "Clients", perm: "client" },
    ],
  },
  {
    title: "Administration",
    items: [
      { to: "/users", label: "Users", perm: "user" },
      { to: "/roles", label: "Roles", perm: "role" },
      { to: "/integrations", label: "Integrations", perm: "integration" },
      { to: "/notifications", label: "Notifications", perm: "notification" },
      { to: "/kyc", label: "KYC", perm: "kyc" },
      { to: "/onboarding", label: "Trial Applications", perm: "kyc" },
      { to: "/fcr", label: "Caller Registry", adminOnly: true },
      { to: "/settings", label: "Settings" },
    ],
  },
];

// `children` is only used by App.tsx's `AgentPanelRoute`, which composes
// Layout directly (bypassing nested-route <Outlet/> resolution) so an
// operator's backward-compatible /agent view can share the exact same
// Layout every other operator route gets, without /agent being registered as
// a second physical route (see that file's comment on why it can't be).
// Every other caller nests Layout as a pathless parent Route and passes no
// children, so it renders <Outlet/> exactly as before.
export function Layout({ children }: { children?: ReactNode }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: me } = useQuery({ queryKey: ["me-permissions"], queryFn: getMyPermissions });
  const { data: unreadSummary } = useQuery({
    queryKey: ["messages-unread-summary"],
    queryFn: getUnreadSummary,
    refetchInterval: 10_000,
  });
  const unreadCount = unreadSummary?.unread_count ?? 0;

  const isAdmin = me?.is_admin ?? false;
  const perms = new Set(me?.permissions ?? []);
  const billingMode = me?.billing_mode ?? "flat";
  const isRestricted = me?.is_restricted ?? false;
  const cardPaymentEnabled = me?.card_payment_enabled ?? false;

  // Mirrors the backend's hierarchical `can_access` (app/deps/auth.py): a
  // permission is granted by itself OR by any ancestor, so `campaign` grants
  // `campaign_queuestatus`. An exact `Set.has` was wrong the moment any nav item
  // used a leaf permission — every operator holding the coarse parent would have
  // silently lost the link while the API still served them the page.
  const canAccess = (perm: string) => {
    const parts = perm.split("_");
    for (let i = parts.length; i > 0; i--) {
      if (perms.has(parts.slice(0, i).join("_"))) return true;
    }
    return false;
  };

  const canSee = (item: NavItem) => {
    if (item.alwaysVisible) return true;
    if (item.adminOnly) return isAdmin;
    if (item.billingMode) return isAdmin || billingMode === item.billingMode;
    // Deny-by-default for a restricted login, matching the API: an item with no
    // permission of its own (Dashboard) is open to every other operator, but for
    // a floor supervisor it would be a link to a page that 403s on arrival.
    if (isRestricted) return !!item.perm && canAccess(item.perm);
    if (item.to === "/billing" && cardPaymentEnabled) return true;
    return !item.perm || isAdmin || canAccess(item.perm);
  };

  // Hide a group entirely when the user can see none of its items.
  const groups = navGroups
    .map((g) => ({ ...g, items: g.items.filter(canSee) }))
    .filter((g) => g.items.length > 0);

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Sidebar */}
      <aside className="w-56 shrink-0 bg-white border-r border-gray-200 flex flex-col h-screen sticky top-0">
        <div className="h-14 flex items-center px-4 border-b border-gray-100">
          <span className="font-bold text-brand text-lg">Reliadata</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {groups.map((group, gi) => (
            <div key={group.title ?? `g${gi}`} className="mb-3">
              {group.title && (
                <div className="px-4 mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {group.title}
                </div>
              )}
              {group.items.map((item) => {
                const isMessages = item.to === "/messages";
                const flashUnread = isMessages && unreadCount > 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-4 py-1.5 text-sm border-l-2 transition ${
                        isActive
                          ? "border-brand bg-brand/5 text-brand font-medium"
                          : flashUnread
                          ? "border-red-600 bg-red-50 text-red-700 font-bold animate-pulse"
                          : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`
                    }
                  >
                    <span className="flex items-center gap-1.5">
                      {isMessages && <span>✉</span>}
                      <span>{item.label}</span>
                    </span>
                    {flashUnread && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-red-600 text-white shadow-xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                        {unreadCount} NEW
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      {/* Content column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6 gap-3 sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/messages")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition border ${
                unreadCount > 0
                  ? "bg-red-600 text-white border-red-700 shadow-sm animate-pulse"
                  : "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
              }`}
            >
              <span>✉ Inbox</span>
              {unreadCount > 0 ? (
                <span className="px-1.5 py-0.5 rounded-full bg-white text-red-700 font-extrabold text-[11px]">
                  {unreadCount} Unread
                </span>
              ) : (
                <span className="text-gray-400 font-normal">0 unread</span>
              )}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {user?.first_name || user?.email || `User ${user?.user_id}`}
              {isAdmin && <span className="ml-2 text-[10px] uppercase bg-brand/10 text-brand rounded px-1.5 py-0.5">admin</span>}
            </span>
            <button
              onClick={handleSignOut}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm text-gray-700"
            >
              Sign out
            </button>
          </div>
        </header>
        {unreadCount > 0 && location.pathname !== "/messages" && (
          <div
            onClick={() => navigate("/messages")}
            className="cursor-pointer bg-gradient-to-r from-red-600 via-amber-600 to-red-600 text-white px-6 py-2.5 flex items-center justify-between shadow-sm animate-pulse"
          >
            <div className="flex items-center gap-2.5 text-sm font-semibold truncate">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-white animate-ping shrink-0" />
              <span>
                New Message{unreadCount > 1 ? `s (${unreadCount})` : ""} from{" "}
                <strong>{unreadSummary?.latest_sender || "Support"}</strong>:{" "}
                <span className="underline">{unreadSummary?.latest_subject}</span>
              </span>
            </div>
            <span className="shrink-0 ml-4 px-3 py-1 rounded-md bg-white text-red-700 text-xs font-bold shadow-2xs">
              Open Inbox →
            </span>
          </div>
        )}
        <main className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-7xl mx-auto">
            {children ?? <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
}
